// src/components/SignInForm.tsx
"use client";

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SubmitHandler } from "react-hook-form";
import { PiArrowRightBold } from "react-icons/pi";
import { Password, Button, Input } from "rizzui";
import { Form } from "@core/ui/form";
import { routes } from "../../../config/routes";
import { loginSchema, LoginSchema } from "../../../validators/login.schema";
import { login } from "../../api/login";
import toast from "react-hot-toast";
import { queryClient } from "../../../../src/queryClient";
import { fetchAllUsers } from "../../api/users";
import { fetchAllCampaigns } from "../../api/campaign";
import { fetchAllPayments } from "../../api/payments";
import { fetchAllWithdrawals } from "../../api/withdrawals";
import LoadingDots from "../../../components/loading-dot";

export default function SignInForm() {
  const [resetValues, setResetValues] = useState<Partial<LoginSchema>>({});

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const onSubmit: SubmitHandler<LoginSchema> = async (data) => {
    setLoading(true);
    try {
      const {
        success,
        message,
        data: { token },
      } = await login(data.username, data.password);
      if (!success) {
        setError("An error occurred, please try again.");
        throw new Error(message);
      }
      sessionStorage.setItem("admin_token", token);
      toast.success("Logged in!");

      // GET ALL INITAILLY DATA
      await Promise.all([
        await Promise.all([
          queryClient.prefetchQuery({
            queryKey: ["users"],
            queryFn: fetchAllUsers,
          }),
          queryClient.prefetchQuery({
            queryKey: ["campaigns"],
            queryFn: fetchAllCampaigns,
          }),
          queryClient.prefetchQuery({
            queryKey: ["payments"],
            queryFn: fetchAllPayments,
          }),
          queryClient.prefetchQuery({
            queryKey: ["withdrawals"],
            queryFn: fetchAllWithdrawals,
          }),
        ]),
      ]);
      toast.success("All data fatched");
      navigate("/", { replace: true });
    } catch (err: any) {
      toast.error(err.message ?? "Login failed, try again");
      setError(err.message ?? "Login failed, try again");
      setResetValues({ password: "" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form<LoginSchema>
      validationSchema={loginSchema}
      resetValues={resetValues}
      onSubmit={onSubmit}
      useFormProps={{ mode: "onChange" }}
      className="space-y-5"
    >
      {({ register, formState: { errors } }) => (
        <>
          <Input
            type="text"
            size="lg"
            label="Username"
            placeholder="Enter your username"
            autoComplete="off"
            inputClassName="text-sm"
            {...register("username")}
            error={errors.username?.message}
          />
          <Password
            label="Password"
            placeholder="Enter your password"
            size="lg"
            inputClassName="text-sm"
            autoComplete="current-password"
            {...register("password")}
            error={errors.password?.message}
          />
          <div className="h-[20px]">
            {error && (
              <div className="text-md font-semibold text-red-500">{error}</div>
            )}
          </div>

          <div className="flex justify-end">
            <Link
              to={routes.auth.forgotPassword1}
              className="text-sm font-semibold text-blue underline hover:text-gray-900"
            >
              Forgot Password?
            </Link>
          </div>
          <Button
            className="w-full flex items-center justify-center gap-2"
            type="submit"
            size="lg"
          >
            <span>Sign in</span>
            {loading ? (
              <LoadingDots />
            ) : (
              <PiArrowRightBold className="mt-0.5 h-3.5 w-3.5" />
            )}
          </Button>
        </>
      )}
    </Form>
  );
}
